import * as React from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { DailyTotal, UpsellsResponse } from './types';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ChartTooltip, Legend);

// Upsell money per day over the page's period: what was charged, the
// estimated contribution margin (collector economics.js), running totals, and
// both per view. Each series can be switched on or off.

type Day = { date: string; viewed: number; accepted: number; revenue: number; margin: number | null };

type SeriesKey = 'revenue' | 'margin' | 'cumRevenue' | 'cumMargin' | 'revenuePerView' | 'marginPerView';

const SERIES: { key: SeriesKey; label: string; color: string; type: 'bar' | 'line'; axis: 'daily' | 'cumulative' | 'perView'; on: boolean }[] = [
  { key: 'revenue', label: 'Revenue', color: '#4F46E5', type: 'bar', axis: 'daily', on: true },
  { key: 'margin', label: 'Est. margin', color: '#10B981', type: 'bar', axis: 'daily', on: true },
  { key: 'cumRevenue', label: 'Cumulative revenue', color: '#312E81', type: 'line', axis: 'cumulative', on: true },
  { key: 'cumMargin', label: 'Cumulative margin', color: '#047857', type: 'line', axis: 'cumulative', on: false },
  { key: 'revenuePerView', label: 'Revenue / view', color: '#F59E0B', type: 'line', axis: 'perView', on: false },
  { key: 'marginPerView', label: 'Margin / view', color: '#DC2626', type: 'line', axis: 'perView', on: false },
];

const AXIS_TITLES = { daily: 'Per day', cumulative: 'Cumulative', perView: 'Per view' };

const dollars = (value: number) =>
  `${value < 0 ? '−' : ''}$${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: Math.abs(value) < 100 ? 2 : 0,
    maximumFractionDigits: Math.abs(value) < 100 ? 2 : 0,
  })}`;

// Every date from..to, so a day without an upsell reads as $0, not as missing.
function datesBetween(from: string, to: string) {
  const dates: string[] = [];
  for (let at = new Date(`${from}T12:00:00Z`); at <= new Date(`${to}T12:00:00Z`); at.setUTCDate(at.getUTCDate() + 1)) {
    dates.push(at.toISOString().slice(0, 10));
  }
  return dates;
}

// Pure: the period's days, all upsells together. A day's margin is unknown if
// any of its upsells' is.
export function dailyTotals(daily: DailyTotal[], from: string, to: string): Day[] {
  const byDate = new Map<string, Day>();
  for (const row of daily) {
    const day = byDate.get(row.date) ?? { date: row.date, viewed: 0, accepted: 0, revenue: 0, margin: 0 };
    day.viewed += row.viewed;
    day.accepted += row.accepted;
    day.revenue += row.revenue;
    day.margin = day.margin == null || row.margin == null ? null : day.margin + row.margin;
    byDate.set(row.date, day);
  }
  return datesBetween(from, to).map(date => byDate.get(date) ?? { date, viewed: 0, accepted: 0, revenue: 0, margin: 0 });
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Box>
  );
}

export function RevenueChart({ data }: { data: UpsellsResponse }) {
  const [shown, setShown] = React.useState<Record<SeriesKey, boolean>>(
    () => Object.fromEntries(SERIES.map(series => [series.key, series.on])) as Record<SeriesKey, boolean>,
  );
  const days = React.useMemo(() => dailyTotals(data.daily, data.from, data.to), [data]);

  const total = days.reduce(
    (sum, day) => ({
      viewed: sum.viewed + day.viewed,
      accepted: sum.accepted + day.accepted,
      revenue: sum.revenue + day.revenue,
      margin: sum.margin == null || day.margin == null ? null : sum.margin + day.margin,
    }),
    { viewed: 0, accepted: 0, revenue: 0, margin: 0 as number | null },
  );
  const perView = (value: number | null, views: number) => (value == null || !views ? null : value / views);

  const values: Record<SeriesKey, (number | null)[]> = React.useMemo(() => {
    let cumRevenue = 0;
    let cumMargin: number | null = 0;
    const cumulative = days.map(day => {
      cumRevenue += day.revenue;
      cumMargin = cumMargin == null || day.margin == null ? null : cumMargin + day.margin;
      return { cumRevenue, cumMargin };
    });
    return {
      revenue: days.map(day => day.revenue),
      margin: days.map(day => day.margin),
      cumRevenue: cumulative.map(day => day.cumRevenue),
      cumMargin: cumulative.map(day => day.cumMargin),
      revenuePerView: days.map(day => perView(day.revenue, day.viewed)),
      marginPerView: days.map(day => perView(day.margin, day.viewed)),
    };
  }, [days]);

  const active = SERIES.filter(series => shown[series.key]);
  const axesInUse = new Set(active.map(series => series.axis));
  const chartData = {
    labels: days.map(day => day.date),
    datasets: active.map(series => ({
      type: series.type,
      label: series.label,
      data: values[series.key],
      yAxisID: series.axis,
      backgroundColor: series.color,
      borderColor: series.color,
      borderRadius: series.type === 'bar' ? 2 : undefined,
      borderWidth: series.type === 'line' ? 2 : 0,
      pointRadius: series.type === 'line' ? 0 : undefined,
      pointHoverRadius: 4,
      // Monotone: a smooth line that never swings past the points it joins.
      cubicInterpolationMode: 'monotone' as const,
      order: series.type === 'line' ? 0 : 1,
    })),
  };
  const scale = (axis: 'daily' | 'cumulative' | 'perView', position: 'left' | 'right', grid: boolean) => ({
    display: axesInUse.has(axis),
    position,
    title: { display: true, text: AXIS_TITLES[axis], color: '#64748B' },
    grid: { display: grid },
    ticks: { callback: (value: number | string) => dollars(Number(value)) },
  });
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      // A Dashboard-page chart registers chartjs-plugin-datalabels globally.
      datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: any[]) =>
            new Date(`${items[0].label}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }),
          afterTitle: (items: any[]) => {
            const day = days[items[0].dataIndex];
            return `${day.viewed} views · ${day.accepted} accepted`;
          },
          label: (item: any) => ` ${item.dataset.label}: ${item.raw == null ? 'unknown' : dollars(item.raw)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          autoSkipPadding: 12,
          callback: (_: unknown, index: number) =>
            new Date(`${days[index].date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
        },
      },
      daily: { ...scale('daily', 'left', true), beginAtZero: true },
      cumulative: { ...scale('cumulative', 'right', false), beginAtZero: true },
      perView: scale('perView', 'right', false),
    },
  };

  const range = data.days ? `last ${data.days} days` : `${data.from} to ${data.to}`;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-label="upsell revenue chart">
      <Typography variant="subtitle1" fontWeight={600}>Upsell money by day</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        All upsells, {range} (Pacific). Revenue is the extra charged, before tax. Est. margin is revenue less the
        extra product cost (Shopify COGS), the card fee on the charge and, for add-ons, about{' '}
        {data.economics.freightPerUnit == null ? 'one more item' : dollars(data.economics.freightPerUnit)} of freight per
        item added; before refunds.
        {Object.entries(data.economics.swapFreight ?? {}).map(([upsellId, freight]) => (
          <React.Fragment key={upsellId}>
            {' '}
            {upsellId}: the replaced items no longer ship, so their cost is netted out;{' '}
            {freight.perSwap == null
              ? `its box's extra freight isn't counted yet (${freight.sample} invoiced, needs 5).`
              : `its box ships for ${dollars(Math.abs(freight.perSwap))} ${freight.perSwap >= 0 ? 'more' : 'less'} (${freight.sample} invoiced).`}
          </React.Fragment>
        ))}
      </Typography>
      {data.economics.problem && <Alert severity="warning" sx={{ mb: 1.5 }}>{data.economics.problem}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
        <Figure label="Revenue" value={dollars(total.revenue)} hint={`${total.accepted} accepted`} />
        <Figure label="Est. margin" value={total.margin == null || data.economics.problem ? 'unknown' : dollars(total.margin)} />
        <Figure label="Revenue / view" value={total.viewed ? dollars(total.revenue / total.viewed) : '—'} hint={`${total.viewed} views`} />
        <Figure label="Margin / view" value={total.viewed && total.margin != null ? dollars(total.margin / total.viewed) : '—'} />
      </Box>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }} aria-label="series">
        {SERIES.map(series => (
          <Chip
            key={series.key}
            size="small"
            label={series.label}
            onClick={() => setShown(current => ({ ...current, [series.key]: !current[series.key] }))}
            variant={shown[series.key] ? 'filled' : 'outlined'}
            sx={
              shown[series.key]
                ? { bgcolor: series.color, color: '#fff', '&:hover': { bgcolor: series.color, opacity: 0.85 } }
                : { borderColor: series.color, color: series.color }
            }
            aria-pressed={shown[series.key]}
          />
        ))}
      </Stack>
      <Box sx={{ height: 300 }}>
        {active.length ? (
          <Chart type="bar" data={chartData as any} options={options as any} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 4, textAlign: 'center' }}>
            Pick a series above to plot it.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
